// src/services/atelier2Service.js
import api from '../api/apiClient';
import { getUserFriendlyErrorMessage, showErrorNotification } from '../utils/errorHandler';

/**
 * Service for handling Atelier 2 components
 */
const atelier2Service = {
  /**
   * Get threat categories for an analysis
   * @param {string} analysisId - Analysis ID
   * @returns {Promise<{definitions: {builtIns: Array, custom: Array}, selected: Array}>}
   */
  getThreatCategories: async (analysisId) => {
    try {
      const response = await api.get(`/analyses/${analysisId}/atelier2/threat-categories`);
      const data = response.data || {};

      // Defensive defaults — backend always returns an object but old code might still call this with cache.
      return {
        definitions: {
          builtIns: Array.isArray(data.definitions?.builtIns) ? data.definitions.builtIns : [],
          custom: Array.isArray(data.definitions?.custom) ? data.definitions.custom : []
        },
        selected: Array.isArray(data.selected) ? data.selected : [],
        hidden: Array.isArray(data.hidden) ? data.hidden : []
      };
    } catch (error) {
      console.error('Error getting threat categories:', error.message);

      // Provide user-friendly error messages
      if (error.response) {
        const status = error.response.status;
        const serverMessage = error.response.data?.message;

        if (status === 404) {
          throw new Error('Analyse non trouvée. Veuillez vérifier que l\'analyse existe et que vous y avez accès.');
        } else if (status === 403) {
          throw new Error('Accès refusé. Vous n\'avez pas les permissions nécessaires pour accéder à cette analyse.');
        } else if (status === 401) {
          throw new Error('Session expirée. Veuillez vous reconnecter pour continuer.');
        } else if (status >= 500) {
          throw new Error(serverMessage || 'Erreur du serveur. Veuillez réessayer dans quelques instants.');
        } else {
          throw new Error(serverMessage || 'Une erreur s\'est produite lors de la récupération des données.');
        }
      } else if (error.request) {
        throw new Error('Impossible de contacter le serveur. Veuillez vérifier votre connexion internet et réessayer.');
      } else {
        throw new Error('Une erreur inattendue s\'est produite. Veuillez rafraîchir la page et réessayer.');
      }
    }
  },

  /**
   * Save threat categories for an analysis
   * @param {string} analysisId - Analysis ID
   * @param {{selected?: string[], customCategories?: Array, hidden?: string[]}} payload
   * @returns {Promise<Object>}
   */
  saveThreatCategories: async (analysisId, payload) => {
    try {
      const body = {
        selected: Array.isArray(payload.selected) ? payload.selected : [],
        customCategories: Array.isArray(payload.customCategories) ? payload.customCategories : [],
        hidden: Array.isArray(payload.hidden) ? payload.hidden : []
      };
      const response = await api.post(`/analyses/${analysisId}/atelier2/threat-categories`, body);

      return response;
    } catch (error) {
      console.error('Error saving threat categories:', error.message);

      if (error.response) {
        const status = error.response.status;
        const serverMessage = error.response.data?.message;
        const isRetryable = error.response.data?.retryable;

        if (status === 400) {
          throw new Error(serverMessage || 'Les données fournies ne sont pas valides. Veuillez vérifier le format des catégories.');
        } else if (status === 403) {
          throw new Error('Accès refusé. Vous n\'avez pas les permissions pour modifier cette analyse.');
        } else if (status === 409) {
          throw new Error(serverMessage || 'Conflit de données. Une catégorie avec ce nom existe peut-être déjà.');
        } else if (status === 413) {
          throw new Error('Les données sont trop volumineuses. Veuillez réduire le nombre de catégories ou leur taille.');
        } else if (status >= 500) {
          const retryMessage = isRetryable ? ' Veuillez réessayer dans quelques instants.' : ' Veuillez contacter le support technique.';
          throw new Error((serverMessage || 'Erreur du serveur lors de la sauvegarde.') + retryMessage);
        } else {
          throw new Error(serverMessage || 'Impossible de sauvegarder les catégories de menaces.');
        }
      } else if (error.request) {
        throw new Error('Impossible de contacter le serveur pour sauvegarder. Vos modifications n\'ont pas été enregistrées. Veuillez vérifier votre connexion et réessayer.');
      } else {
        throw new Error('Erreur lors de la préparation des données. Veuillez réessayer ou rafraîchir la page.');
      }
    }
  },

  /**
   * Get sources de risque for an analysis
   * @param {string} analysisId - Analysis ID
   * @returns {Promise<Array>} - Sources de risque
   */
  getSourcesRisque: async (analysisId) => {
    try {
      const response = await api.get(`/analyses/${analysisId}/atelier2/sources-risque`);

      let sources = [];
      if (Array.isArray(response)) {
        sources = response;
      } else if (response && typeof response === 'object') {
        if (Array.isArray(response.data)) {
          sources = response.data;
        } else if (response.data?.sources && Array.isArray(response.data.sources)) {
          sources = response.data.sources;
        } else if (response.sources && Array.isArray(response.sources)) {
          sources = response.sources;
        }
      }

      return sources;
    } catch (error) {
      console.error('Error getting sources de risque:', error);
      if (process.env.NODE_ENV === 'development') {
        return [];
      }
      throw error;
    }
  },

  /**
   * Save sources de risque for an analysis
   * @param {string} analysisId - Analysis ID
   * @param {Array} sources - Sources de risque
   * @returns {Promise<Object>} - Response
   */
  saveSourcesRisque: async (analysisId, sources, radarImageData = null) => {
    console.log("saveSourcesRisque called with analysisId:", analysisId, "sources:", sources);
    try {
      // Format the data according to the expected structure
      const formattedData = {
        sources: sources
      };

      // If radar image data is provided, save it separately
      if (radarImageData) {
        console.log("📸 Saving radar visualization image...");
        try {
          const imagePayload = {
            imageData: radarImageData,
            timestamp: new Date().toISOString()
          };
          await api.post(`/analyses/${analysisId}/atelier2/radar-visualization`, imagePayload);
          console.log("✅ Radar visualization image saved successfully");
        } catch (imageError) {
          console.warn("⚠️ Failed to save radar visualization image:", imageError);
          // Don't fail the entire save operation if image save fails
        }
      }

      console.log("Sending formatted data to API:", formattedData);
      const response = await api.post(`/analyses/${analysisId}/atelier2/sources-risque`, formattedData);
      console.log("API response from saving sources de risque:", response);

      return response;
    } catch (error) {
      console.error('Error saving sources de risque:', error);
      // Return success for development
      if (process.env.NODE_ENV === 'development') {
        return { success: true, data: sources };
      }
      throw error;
    }
  },

  /**
   * Get couples SR/OV for an analysis
   * @param {string} analysisId - Analysis ID
   * @returns {Promise<Array>} - Couples SR/OV
   */
  getCouplesSROV: async (analysisId) => {
    try {
      const response = await api.get(`/analyses/${analysisId}/atelier2/couples-srov`);

      let couples = [];
      if (Array.isArray(response)) {
        couples = response;
      } else if (response && typeof response === 'object') {
        if (Array.isArray(response.data)) {
          couples = response.data;
        } else if (response.data?.couples && Array.isArray(response.data.couples)) {
          couples = response.data.couples;
        } else if (response.couples && Array.isArray(response.couples)) {
          couples = response.couples;
        }
      }

      return couples;
    } catch (error) {
      console.error('Error getting couples SR/OV');
      if (process.env.NODE_ENV === 'development') {
        return [];
      }
      throw error;
    }
  },

  /**
   * Save couples SR/OV for an analysis
   * @param {string} analysisId - Analysis ID
   * @param {Array} couples - Couples SR/OV
   * @returns {Promise<Object>} - Response
   */
  saveCouplesSROV: async (analysisId, couples) => {
    try {
      // Ensure all required fields are present in each couple
      const formattedCouples = couples.map(couple => ({
        id: couple.id,
        sourceId: couple.sourceId,
        sourceName: couple.sourceName,
        sourceCategory: couple.sourceCategory,
        objectifVise: couple.objectifVise || '',
        objectifViseCategory: couple.objectifViseCategory || '',
        motivation: couple.motivation || 'faible',
        activite: couple.activite || 'faible',
        ressources: couple.ressources || 'faibles',
        niveau: couple.niveau || 1,
        selected: couple.selected || false
      }));

      const response = await api.post(`/analyses/${analysisId}/atelier2/couples-srov`, { couples: formattedCouples });

      return response;
    } catch (error) {
      console.error('Error saving couples SR/OV'); // Removed error object from log
      // Return success for development
      if (process.env.NODE_ENV === 'development') {
        return { success: true, data: couples };
      }
      throw error;
    }
  },

  /**
   * Get selected couples SR/OV for an analysis
   * This function now supports both the old format (array of IDs) and the new format (array of mapping objects)
   * @param {string} analysisId - Analysis ID
   * @returns {Promise<Array>} - Selected couples SR/OV or source risk to dreaded event mappings
   */
  getSelectedCouplesSROV: async (analysisId) => {
    try {
      const response = await api.get(`/analyses/${analysisId}/atelier2/selected-couples-srov`);

      // Handle different response formats
      let data = [];

      if (response && typeof response === 'object') {
        // Check for new format (mappings)
        if (response.mappings && Array.isArray(response.mappings)) {
          data = response.mappings;
        }
        // Check for old format directly in response
        else if (response.selectedCouples && Array.isArray(response.selectedCouples)) {
          data = response.selectedCouples;
        }
        // Check for attackPaths format
        else if (response.attackPaths && Array.isArray(response.attackPaths)) {
          data = response.attackPaths;
        }
        // Check for data property
        else if (response.data) {
          // Check for new format in data
          if (response.data.mappings && Array.isArray(response.data.mappings)) {
            data = response.data.mappings;
          }
          // Check for old format in data
          else if (response.data.selectedCouples && Array.isArray(response.data.selectedCouples)) {
            data = response.data.selectedCouples;
          }
          // Check for attackPaths in data
          else if (response.data.attackPaths && Array.isArray(response.data.attackPaths)) {
            data = response.data.attackPaths;
          }
          // If data is an array, use it directly
          else if (Array.isArray(response.data)) {
            data = response.data;
          }
        }
        // If response is an array, use it directly
        else if (Array.isArray(response)) {
          data = response;
        }
      }

      return data;
    } catch (error) {
      console.error('Error getting selected couples SR/OV'); // Removed error object from log
      // Return empty array for development
      if (process.env.NODE_ENV === 'development') {
        return [];
      }
      throw error;
    }
  },

  /**
   * Save selected couples SR/OV for an analysis
   * This function now supports both the old format (array of IDs) and the new format (array of mapping objects)
   * @param {string} analysisId - Analysis ID
   * @param {Array|Object} selectedCouples - Selected couples SR/OV or source risk to dreaded event mappings
   * @returns {Promise<Object>} - Response
   */
  saveSelectedCouplesSROV: async (analysisId, selectedCouples) => {
    try {
      let formattedData;

      // Check if we're dealing with a combined format (object with both selectedCouples and mappings)
      if (typeof selectedCouples === 'object' && !Array.isArray(selectedCouples) &&
        selectedCouples.selectedCouples !== undefined && selectedCouples.mappings !== undefined) {
        // Already formatted correctly
        formattedData = selectedCouples;
      }
      // Check if we're dealing with the new mapping format (objects with sourceId and dreadedEventId)
      else if (Array.isArray(selectedCouples) && selectedCouples.length > 0 &&
        typeof selectedCouples[0] === 'object' &&
        (selectedCouples[0].sourceId !== undefined)) {
        formattedData = { mappings: selectedCouples }; // New format: array of mapping objects
      }
      // Old format: array of IDs
      else {
        formattedData = { selectedCouples };
      }

      const response = await api.post(`/analyses/${analysisId}/atelier2/selected-couples-srov`, formattedData);

      return response;
    } catch (error) {
      console.error('Error saving selected couples SR/OV'); // Removed error object from log
      // Return success for development
      if (process.env.NODE_ENV === 'development') {
        return { success: true, data: selectedCouples };
      }
      throw error;
    }
  },

  /**
   * Get saved radar visualization image for an analysis
   * @param {string} analysisId - Analysis ID
   * @returns {Promise<string|null>} - Radar image data URL or null if not found
   */
  getRadarVisualization: async (analysisId) => {
    try {
      const response = await api.get(`/analyses/${analysisId}/atelier2/radar-visualization`);

      const imageData = response?.imageData || null;

      return imageData;
    } catch (error) {
      return null;
    }
  }
};

export default atelier2Service;
